"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.openedEntrySchema = exports.entryParamsSchema = exports.vitalsEntrySchema = exports.alertSummarySchema = exports.savedReadingSchema = exports.vitalsEntryInputSchema = exports.notifyPersonSchema = exports.NOTIFY_PEOPLE_MAX = exports.bloodPressureRowSchema = exports.captureRowSchema = exports.SINGLE_ROW_VITALS = void 0;
/**
 * `RPM-001` — the shape `POST /api/rpm/v1/entries` accepts and returns (`J-212`).
 *
 * THE ROUTE IS `/entries`, NOT `/readings`, AND THAT IS A RECORDED DECISION. `01-RPM-SPEC.md:619`
 * names `POST /api/rpm/v1/readings`; four other statements name `/entries` — `RPM-098:999` carries
 * the contract, its acceptance row 10 names the path, and `RPM-005:1794` and `RPM-100:1208` both say
 * "the route of `RPM-098`". `/readings` is the reading COLLECTION in `RPM-004:1722` and
 * `RPM-101:1308`, and one submission writes a group with several readings, so a collection-named
 * route cannot be the one that creates a group. `:619` is a corpus defect (`D-791`).
 *
 * THE BODY IS STRICT, AND `RPM-083` IS WHY. On the pinned Zod 4.5.4 a plain `z.object` given an
 * undeclared key reports success and strips it; `z.strictObject` reports `unrecognized_keys`.
 * `RPM-083` acceptance row 2 says a body containing `nurseName` is REJECTED, not quietly accepted,
 * and `actor-identity.integration.test.ts` asserts every RPM input body is strict. The actor is the
 * session's `user.id` and is never a field here.
 *
 * THE IDENTIFIERS ARE THE MIRROR'S OWN, and neither is a UUID. `carease.facilities.id` and
 * `carease.patients.id` are `varchar(20)` and no foreign key reaches either (`CR-23`, N15), so the
 * lengths here are the mirror's and the validation is a bound, not a format.
 *
 * THE BODY WIDENED IN `J-215` FROM ONE READING TO ONE SUBMISSION, and `RPM-098` is why: its
 * acceptance row 2 wants two rows sharing one group from `148/70`, and its row 5 refuses a save with
 * a missing time "including the rows that were complete, because one entry is one transaction". A
 * one-reading body cannot express either. `J-212`'s `readingEntryInputSchema` and `readingEntrySchema`
 * are GONE rather than deprecated beside it: two shapes on one path is two contracts, and `knip`
 * refuses an export nothing imports. There is one route, and it takes one submission.
 *
 * WHAT THIS CONTRACT DOES NOT CARRY YET: the resolved instant and its zone (`RPM-021`, `J-218`). The
 * person to notify arrived in `J-216` (`RPM-099`) and the form's own submission key in `J-261`
 * (`RPM-005`); both are below. Each extended this file; none replaced it.
 */
const zod_1 = require("zod");
const rpm_enums_js_1 = require("./db/rpm-enums.js");
const rpm_entry_time_js_1 = require("./rpm-entry-time.js");
/** The mirror's identifier width. `carease.facilities.id` and `carease.patients.id` are varchar(20). */
const MIRROR_ID = zod_1.z.string().min(1).max(20);
/**
 * The key a capture row keeps for as long as the entry exists (`RPM-005` row 3d, `CR-44`).
 *
 * "Every other row keeps the key it was first stored under, and a key is never reused for another
 * vital." A key derived from the row's POSITION cannot keep that promise: removing the first row
 * renumbers every row after it. So the form mints one key per row when the row is added and sends it
 * on every store, and the reading's `source_reading_key` is derived from it. The character set keeps
 * the separator `:` out, so a row key can never forge a blood-pressure half's suffix.
 *
 * OPTIONAL, because a client that sends none keeps today's position keys unchanged.
 */
const ROW_KEY = zod_1.z
    .string()
    .min(1)
    .max(64)
    .regex(/^[A-Za-z0-9_-]+$/);
/**
 * The four vitals that take one capture row each.
 *
 * `BP_SYSTOLIC` and `BP_DIASTOLIC` are absent BY DESIGN: `RPM-098`'s Operator surface puts them "on
 * the grouped blood-pressure row", one control for the pair, so they arrive through `bloodPressure`
 * below and never as a row of their own. `ACVPU` is reserved and is not offered in v1 (`RPM-091`).
 */
exports.SINGLE_ROW_VITALS = ["HR", "RR", "TEMP", "SPO2"];
/**
 * One capture row as the form submits it.
 *
 * THE UNIT TRAVELS AS CAPTURED AND IS OPTIONAL. Each row's unit "defaults to that definition's
 * `ucum_unit` and accepts a listed alias; nothing is converted at capture". An absent unit means the
 * nurse left the default, which is the canonical code; a present one is stored byte for byte and
 * resolved into `CANONICAL`, `ALIASED` or `UNRESOLVABLE` (`RPM-002`).
 */
exports.captureRowSchema = zod_1.z
    .strictObject({
    /**
     * The vital she picked, or the empty string when she has not picked one yet.
     *
     * THE EMPTY STRING IS ACCEPTED BY THE SHAPE AND REFUSED BY THE SERVICE, for the same reason the
     * date and the time are: a row with a value and no vital is one of `RPM-098`'s absences, and its
     * refusal has to name what is missing rather than report that the body did not match a schema.
     * The form sends the row rather than dropping it, because dropping it would lose what she typed.
     */
    vitalCode: zod_1.z.union([zod_1.z.enum(exports.SINGLE_ROW_VITALS), zod_1.z.literal("")]),
    /**
     * As typed. The number is extracted from it by `RPM-009`, never by the client.
     *
     * NO `min(1)`, AND THAT IS ON PURPOSE. An empty value is one of `RPM-098`'s three structural
     * absences and its refusal has to NAME the absence — "A capture row has no value in it" — which
     * is a sentence the service writes, not a length the schema reports. One refusal path, in the
     * place that knows what the absence means; the length bound stays.
     */
    valueText: zod_1.z.string().max(200),
    /** As typed, when she changed it. Absent means the definition's canonical code. */
    unit: zod_1.z.string().min(1).max(40).optional(),
    /** The row's own key, minted when the row was added (`RPM-005` row 3d). */
    rowKey: ROW_KEY.optional(),
})
    .meta({ id: "CaptureRow", description: "One vital as one row of the form" });
/**
 * The grouped blood-pressure row: ONE control, one string, two readings.
 *
 * `148/70` is acceptance row 2 and `148/` is row 3. The split happens on the server, because the pair
 * status it produces — `COMPLETE`, `PARTIAL_SYSTOLIC_ONLY`, `PARTIAL_DIASTOLIC_ONLY` — is a stored
 * column and a client that computed it would be a second place the rule lives.
 */
exports.bloodPressureRowSchema = zod_1.z
    .strictObject({
    /** As typed, with the separator as typed. Stored whole as the group's `captured_full_text`. */
    valueText: zod_1.z.string().max(200),
    unit: zod_1.z.string().min(1).max(40).optional(),
    /** The row's own key; its two halves are stored under it with `:SYS` and `:DIA`. */
    rowKey: ROW_KEY.optional(),
    /**
     * Where the row sits among the form's rows, counted from 1, so its two readings are stored in
     * the place she typed them (`J-552`). The capture rows' own order is the order of `rows`; this
     * is the one row that travels outside it. Absent means after every capture row.
     */
    position: zod_1.z.int().min(1).max(21).optional(),
})
    .meta({ id: "BloodPressureRow", description: "The grouped blood-pressure row, as one string" });
/**
 * How many people the alert's own surface offers. `RPM-099`'s Operator surface repeats the fieldset
 * "for a second and a third person", and the table itself bounds nothing — `sequence_no` is any
 * positive integer. Three is therefore the SURFACE's number and lives here rather than in the
 * database (`D-837`, `J-216`).
 */
exports.NOTIFY_PEOPLE_MAX = 3;
/**
 * One person to be told about this alert, as the nurse types them.
 *
 * NOTHING ACCUMULATES. There is no directory, no contact record and no reuse (`D7`, `CR-25`): these
 * three values are typed on this entry and the next entry starts empty. The rows they become are
 * `rpm_alert_notify_people`, one per person, in the order she typed them.
 *
 * NEITHER CONTACT FIELD IS FORMAT-CHECKED, AND THAT IS DELIBERATE. `C21` asks for at least one of
 * the two, never for a shape, and whether a destination may be used is the facility's policy
 * (`RPM-036`). A `z.email()` here would refuse an internal address a building really uses, which is
 * a refusal the corpus does not ask for. The bounds are lengths (`D-839`, `J-216`).
 *
 * NO `min(1)` ON THE NAME, for the reason the date and the capture row carry none: the absence is
 * named by the service — "a person to notify has no name" — rather than reported as a schema length.
 */
exports.notifyPersonSchema = zod_1.z
    .strictObject({
    name: zod_1.z.string().max(200),
    email: zod_1.z.string().max(320).optional(),
    // A LINE OF TEXT, NOT A FIELD (`RPM-026`): "PHONE 973 694-2100 ext 2074 (1st Floor UM)" is the
    // block's own example and runs to 42 characters, so the cap is the name's, not a number's.
    phone: zod_1.z.string().max(200).optional(),
})
    .meta({ id: "NotifyPerson", description: "One person to be told about this alert, as typed" });
/**
 * An entry stored against the wrong resident, and why she is entering it again (`RPM-101` row 6,
 * `RPM-005` row 5b).
 *
 * THE RESIDENT IS A PICK, NOT A CORRECTABLE FACT, so the right entry is a NEW entry — its own key,
 * its own completion record — and this names the wrong one it supersedes. The reason is required
 * because the two entries name different residents, and it is stored on the wrong record's
 * transition row, where the lane override keeps its reason too. NO `id`, for the reason the body
 * schema carries none: an input component name collides with the response registry.
 */
const identityCorrectionSchema = zod_1.z
    .strictObject({
    supersedesRpmAlertId: zod_1.z.uuid(),
    reason: zod_1.z.string().min(1).max(500),
})
    .meta({ description: "The wrong entry this one supersedes, and the stated reason" });
/**
 * One submission of the vitals form.
 *
 * ONE DATE AND ONE TIME FOR THE WHOLE SUBMISSION, which is what the form shows: the Operator surface
 * lists "field: Time taken" once, above the capture rows, not per row. They are TEXT as typed,
 * because `RPM-021` (`J-218`) owns turning them into an instant.
 *
 * `sourceSystemId` IS PRESENT AND IS ALMOST ALWAYS ABSENT. `RPM-001`'s Operator surface row is "None.
 * The nurse is never asked for the source platform", and `RPM-098` states the one exception in the
 * same breath: "Any facility resolving to more than one source system must have the source question
 * asked: for those buildings alone the form shows the pick". So the field exists for those buildings
 * and is refused for every other, which is a rule the server enforces rather than a hint the client
 * follows.
 *
 * `oxygenStatus` travels only where an `SPO2` row does, which is the Operator surface's own condition
 * ("where an SpO₂ value is present"). What it BLOCKS is `RPM-024`'s (`J-275`, chunk 3); what it is
 * here is a column on the row.
 */
exports.vitalsEntryInputSchema = zod_1.z
    .strictObject({
    /**
     * The key the form minted WHEN IT OPENED, and never regenerated (`RPM-005`).
     *
     * A key minted at send time would make a double-click two reading sets, which is the failure this
     * field exists to prevent: `(tenant_id, source_system_id, source_reading_key)` is unique, and the
     * per-reading key the server derives from this one is what a replay lands on. It is also the
     * alert's `workup_key`, so a re-submitted entry updates its workup rather than opening a second.
     *
     * NO `min(1)` HERE. An empty key is `400` with a sentence that names it, for the reason the date
     * and the capture row carry none: the absence is named by the service, not reported as a length.
     */
    submissionKey: zod_1.z.string().max(64),
    facilityId: MIRROR_ID,
    careasePatientId: MIRROR_ID,
    /**
     * The care unit the resident is on (`RPM-031`, `CR-43`): an active unit of the picked building.
     * OPTIONAL HERE, REQUIRED BY THE SAVE, so an entry without one is `422 CARE_UNIT_REQUIRED` naming
     * it — the block's row 11 — rather than a schema `400` that names a path.
     */
    careUnitId: zod_1.z.uuid().optional(),
    /**
     * The moment, as typed. `min(1)` is deliberately absent from both.
     *
     * `RPM-098` rows 4 and 5 want a `400` that NAMES what is missing — the date, the time — and a
     * schema reporting "expected >= 1 characters" names a field rather than an absence. The service
     * refuses both, and it also catches a value that is only spaces, which a length cannot.
     */
    takenAtLocalDate: zod_1.z.string().max(40),
    takenAtLocalTime: zod_1.z.string().max(40),
    /** At least one, because acceptance row 4 refuses a save with no capture row at all. */
    rows: zod_1.z.array(exports.captureRowSchema).max(20).default([]),
    /** At most one: the form has one grouped blood-pressure control. */
    bloodPressure: exports.bloodPressureRowSchema.optional(),
    /** Only for a building whose mirror row names more than one platform. */
    sourceSystemId: zod_1.z.uuid().optional(),
    oxygenStatus: zod_1.z.enum(rpm_enums_js_1.oxygenStatus.enumValues).optional(),
    /**
     * Who is to be told, typed on this entry (`RPM-099`).
     *
     * AT LEAST ONE, AND THE SERVICE IS WHAT SAYS SO. `RPM-100`'s Preconditions row makes a person to
     * notify one of the four things supplied before a save, so an entry naming nobody is refused —
     * with a sentence, like the other absences, rather than as an array length.
     */
    notifyPeople: zod_1.z.array(exports.notifyPersonSchema).max(exports.NOTIFY_PEOPLE_MAX).default([]),
    /**
     * Every save creates the entry's completion record (`RPM-100`, `CR-44`), so `WORK_UP` is the one
     * intent and the default: a client that sends nothing gets the record.
     *
     * `RECORD` IS KEPT ONLY TO BE REFUSED (`J-537`): `422 COMPLETION_RECORD_REQUIRED`, naming why,
     * rather than a schema `400` naming a path. It was the save that stored a reading with no record,
     * and a client still sending it is told the save it asked for is gone.
     */
    intent: zod_1.z.enum(["RECORD", "WORK_UP"]).default("WORK_UP"),
    /** Present only when this entry re-enters one stored against the wrong resident. */
    identityCorrection: identityCorrectionSchema.optional(),
})
    .meta({ description: "One submission of the vitals form: one moment, several vitals" });
/** One saved row, as the form reads it back. The group's fields travel beside it, never inside it. */
exports.savedReadingSchema = zod_1.z
    .object({
    vitalReadingId: zod_1.z.uuid(),
    vitalCode: zod_1.z.enum(rpm_enums_js_1.vitalCode.enumValues),
    valueText: zod_1.z.string(),
    valueNumeric: zod_1.z.string().nullable(),
    valueUnit: zod_1.z.string().nullable(),
    unitResolution: zod_1.z.enum(rpm_enums_js_1.unitResolution.enumValues),
    plausibilityStatus: zod_1.z.enum(["PLAUSIBLE", "IMPLAUSIBLE"]),
    /**
     * What this reading scored, and null where it scored nothing.
     *
     * Null carries two different facts and the pair of columns beside it says which: an `IMPLAUSIBLE`
     * reading is never scored (`vr_implausible_unscored`, C4), and a `CLEAR` reading has no band
     * because `CLEAR` is the ABSENCE of one (`cb_no_clear_row`).
     */
    severityBand: zod_1.z.enum(rpm_enums_js_1.severityBand.enumValues).nullable(),
    /** The set it was scored against, pinned at the save instant (`vr_scored_pins_version`, C5). */
    classificationBandSetId: zod_1.z.uuid().nullable(),
    /** Set on the two rows of a blood-pressure pair, null on every other. */
    readingGroupId: zod_1.z.uuid().nullable(),
    /** The entry's completion record, which every save writes with its readings (`RPM-100`). */
    rpmAlertId: zod_1.z.uuid(),
})
    .meta({ id: "SavedReading", description: "One row the submission wrote" });
/**
 * The workup a save opened, as the form reads it back.
 *
 * `lane` MAY BE NULL AND THAT IS NOT AN ABSENT LANE. `rpm_alerts.lane` is nullable and `C3` keeps
 * `GRAY` out of it entirely; a workup whose readings were all implausible carries `MACHINE`, and one
 * whose lane is still being decided carries null rather than a guess.
 */
exports.alertSummarySchema = zod_1.z
    .object({
    rpmAlertId: zod_1.z.uuid(),
    alertState: zod_1.z.enum(rpm_enums_js_1.alertState.enumValues),
    autoLane: zod_1.z.enum(rpm_enums_js_1.alertLane.enumValues).nullable(),
    lane: zod_1.z.enum(rpm_enums_js_1.alertLane.enumValues).nullable(),
    notificationObligationStatus: zod_1.z.enum(rpm_enums_js_1.obligationStatus.enumValues),
    /** Empty where nothing blocks. One code per unmet gate (`RPM-090`). */
    outstandingGateCodes: zod_1.z.array(zod_1.z.string()),
})
    .meta({ id: "AlertSummary", description: "The workup a save opened" });
/**
 * What the save answers.
 *
 * The group is reported when there was one, so the form can show that `148/70` became two rows and
 * which pair status they carry — which is what acceptance rows 2 and 3 assert.
 */
exports.vitalsEntrySchema = zod_1.z
    .object({
    /**
     * The key the form minted WHEN IT OPENED, and never regenerated (`RPM-005`).
     *
     * A key minted at send time would make a double-click two reading sets, which is the failure this
     * field exists to prevent: `(tenant_id, source_system_id, source_reading_key)` is unique, and the
     * per-reading key the server derives from this one is what a replay lands on. It is also the
     * alert's `workup_key`, so a re-submitted entry updates its workup rather than opening a second.
     *
     * NO `min(1)` HERE. An empty key is `400` with a sentence that names it, for the reason the date
     * and the capture row carry none: the absence is named by the service, not reported as a length.
     */
    submissionKey: zod_1.z.string().max(64),
    facilityId: MIRROR_ID,
    careasePatientId: MIRROR_ID,
    takenAtLocalDate: zod_1.z.string(),
    takenAtLocalTime: zod_1.z.string(),
    /**
     * Whether the typed date and time became an instant at the building (`RPM-021`, `J-218`). The
     * contract returns the reason CODE and the surface renders the sentence from it, naming the zone.
     */
    timestampResolution: zod_1.z.enum(rpm_enums_js_1.timestampResolution.enumValues),
    /** The zone the text was read in; null only when there was none (`UNRESOLVED_FACILITY_ZONE`). */
    takenAtZone: zod_1.z.string().nullable(),
    sourceSystemId: zod_1.z.uuid(),
    sourceSystemDisplayName: zod_1.z.string(),
    createdBy: zod_1.z.uuid(),
    readings: zod_1.z.array(exports.savedReadingSchema),
    group: zod_1.z
        .object({
        vitalReadingGroupId: zod_1.z.uuid(),
        pairStatus: zod_1.z.enum(rpm_enums_js_1.bpPairStatus.enumValues),
        capturedFullText: zod_1.z.string(),
    })
        .nullable(),
    /**
     * The entry's completion record, which every save writes (`RPM-100`, `J-537`).
     *
     * `RPM-100`'s API row: "`201` with the created ids and the entry's blocking list (`RPM-090`)".
     * The blocking list is `outstandingGateCodes`, empty where nothing blocks.
     */
    alert: exports.alertSummarySchema,
    /**
     * `CREATED` on the first save of this submission key, `UPDATED` on every replay (`RPM-005`).
     *
     * The route answers `201` and `200` for the same distinction, and the API row says why it is
     * meaningful to the caller: "a `200` means the entry she is looking at was already saved". It is
     * in the body as well as in the status so the form can say so without reading the response code.
     */
    outcome: zod_1.z.enum(["CREATED", "UPDATED"]),
})
    .meta({ id: "VitalsEntry", description: "One saved submission of the vitals form" });
/**
 * The entry named in the path by its completion record (`J-532`): an opaque id, which is what
 * `.claude/rules/phi.md` rule 2 permits in a path. The address of Enter vitals carries the same id.
 */
exports.entryParamsSchema = zod_1.z.strictObject({ rpmAlertId: zod_1.z.uuid() });
/** One current reading of an opened entry, as the form rebuilds its rows from it. */
const openedEntryReadingSchema = zod_1.z.object({
    vitalReadingId: zod_1.z.uuid(),
    /**
     * The capture row's own key, read back from the reading's key, and null where the reading was not
     * stored from a row that carried one — a correction's reading, or one saved before row keys. The
     * form rebuilds a row only from a reading that has one, because only that row can be sent again
     * under the key it was first stored under (`RPM-005` row 3d).
     */
    rowKey: zod_1.z.string().nullable(),
    vitalCode: zod_1.z.enum(rpm_enums_js_1.vitalCode.enumValues),
    valueText: zod_1.z.string(),
    valueUnit: zod_1.z.string().nullable(),
    severityBand: zod_1.z.enum(rpm_enums_js_1.severityBand.enumValues).nullable(),
    /** The blood-pressure pair as typed, on each of its two halves; null on every other reading. */
    capturedFullText: zod_1.z.string().nullable(),
    oxygenStatus: zod_1.z.enum(rpm_enums_js_1.oxygenStatus.enumValues).nullable(),
    /**
     * The reading this one corrects, and null where it corrects none (`RPM-101`'s Operator surface:
     * "state: superseded value — shown beside the new one"). The row its `corrects_reading_id` names,
     * whichever record of the entry's chain it stands on, so a reload at a newer record's address
     * still shows what was superseded. One step back: a correction of a correction names the reading
     * it replaced, not the first one typed.
     */
    supersedes: zod_1.z
        .object({
        vitalReadingId: zod_1.z.uuid(),
        vitalCode: zod_1.z.enum(rpm_enums_js_1.vitalCode.enumValues),
        valueText: zod_1.z.string(),
        severityBand: zod_1.z.enum(rpm_enums_js_1.severityBand.enumValues).nullable(),
    })
        .nullable(),
});
/**
 * One entry as Enter vitals reopens it from its address (`J-532`): the building, the resident, the
 * care unit, the one date and time, the key the form stores under, the current readings and the
 * people she added.
 */
exports.openedEntrySchema = zod_1.z
    .object({
    /** The entry's CURRENT completion record: a correction may have opened a newer one. */
    rpmAlertId: zod_1.z.uuid(),
    alertState: zod_1.z.enum(rpm_enums_js_1.alertState.enumValues),
    /**
     * The key the form stores the entry under, and null where no store of the form opened the
     * entry's first record — one opened over a recorded observation or by a correction alone. A
     * null key is an entry the form can show and correct but not store again.
     */
    submissionKey: zod_1.z.string().nullable(),
    /**
     * Whether the form can store the entry again: its key is known, and every current reading stored
     * under that key carries a row key. A reading stored under a POSITION key cannot be sent again
     * under the key it was stored with, so a store would take it for a row removed (`RPM-005` row
     * 3d); such an entry is shown and corrected, never stored again.
     */
    storable: zod_1.z.boolean(),
    facilityId: MIRROR_ID,
    careasePatientId: MIRROR_ID,
    careUnitId: zod_1.z.uuid().nullable(),
    /** The entry's one date and time as typed; empty where no reading of it stands. */
    takenAtLocalDate: zod_1.z.string(),
    takenAtLocalTime: zod_1.z.string(),
    readings: zod_1.z.array(openedEntryReadingSchema),
    /** The people she added on this entry, in the order she typed them (`RPM-099`). */
    notifyPeople: zod_1.z.array(zod_1.z.object({ name: zod_1.z.string(), email: zod_1.z.string().nullable(), phone: zod_1.z.string().nullable() })),
    /**
     * Time on the entry (`RPM-106`, `J-601`): the total over every record of the entry's chain, and
     * each person's total, visits, and first and last time stored. Enter vitals counts on from it.
     */
    timeOnEntry: rpm_entry_time_js_1.entryTimeSchema,
})
    .meta({ id: "OpenedEntry", description: "One entry, as Enter vitals reopens it" });
//# sourceMappingURL=rpm-entries.js.map